import React, { useRef, memo, useCallback } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { EditBg } from './customEditBg';
import { useDraggable } from '@dnd-kit/core';

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden; /* prevent content from expanding container */
`;

const ImageElement = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  object-fit: contain; /* preserve aspect ratio, no cropping */
  border-radius: 4px;
  max-width: 100%;
  max-height: 100%;
  display: block;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border: 2px dashed #ccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  color: #666;
  font-size: 16px;
`;

type SeqItemLite = {
  id: string | number;
  items?: SeqItemLite[];
  pictures?:
    | {
        id: number | string;
        mediaUrl?: string;
        screenshotUrl?: string;
        name?: string;
      }[]
    | null;
  title?: string | null;
  body?: string | null;
};

type DndImageProps = {
  subItemId?: string | number; // legacy fallback for root placeholder image
  subItem?: SeqItemLite; // direct subItem payload from root
};

export const DndImageComponent = memo(
  ({ subItemId, subItem }: DndImageProps) => {
    const { contentEditable, updateFile } = useEditorStore();

    // Setup drag only in editor mode
    const draggableId = String(subItem?.id ?? subItemId ?? 'image');
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `image:${draggableId}`,
      disabled: !contentEditable
    });

    const divElRef = useRef<HTMLDivElement | null>(null);
    const setRefs = (el: HTMLDivElement | null) => {
      divElRef.current = el;
      setNodeRef(el);
    };

    const pictureData = subItem?.pictures?.[0] ?? null;
    const imageUrl =
      (pictureData?.mediaUrl as string | undefined) ||
      (pictureData?.screenshotUrl as string | undefined);
    const alt = (pictureData?.name as string) || 'Image';

    const handleFilePicked = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (body: { id: string; file: any; type: string }) => {
        const file = body.file;
        if (!file || !file.id) return;
        try {
          const targetId = String(subItem?.id ?? subItemId ?? '');
          if (!targetId) return;
          await updateFile(targetId, file, 'pictures');
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Failed to attach picture to image subitem', e);
        }
      },
      [subItem?.id, subItemId, updateFile]
    );

    return (
      <ImageContainer
        ref={setRefs}
        {...(contentEditable ? { ...listeners, ...attributes } : {})}
        style={{
          opacity: isDragging ? 0.6 : 1,
          cursor: contentEditable
            ? isDragging
              ? 'grabbing'
              : 'grab'
            : 'default',
          touchAction: 'none'
        }}
        data-dnd-draggable={contentEditable ? 'true' : 'false'}
      >
        {imageUrl ? (
          <ImageElement src={imageUrl} alt={alt} />
        ) : (
          <ImagePlaceholder>📷 No image selected</ImagePlaceholder>
        )}
        {contentEditable && (
          <EditBg
            divElRef={divElRef}
            id={String(subItem?.id ?? subItemId ?? '')}
            type="image"
            cb={handleFilePicked}
          />
        )}
      </ImageContainer>
    );
  }
);

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ImagePlaceholderForPreview = styled.div`
  width: 60px;
  height: 40px;
  border: 2px dashed #ccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  color: #666;
  font-size: 12px;
`;

export const createImageComponentPreview = () => {
  return (
    <ImagePreviewContainer>
      <ImagePlaceholderForPreview>📷</ImagePlaceholderForPreview>
      <div style={{ fontSize: '12px', color: '#666' }}>Image</div>
    </ImagePreviewContainer>
  );
};
